import React, { useEffect } from 'react';
import { Col, Form, Popconfirm, Row } from 'antd';

import { Button } from '@nvision/ui';

import { convertToFloat } from '@/utilz';
import DatePicker from '@/views/components/Inputs/DatePicker';
import FormInputNumber from '@/views/components/Inputs/FormInputNumber';
import { useFocusableRef } from '@/views/screens/Claims/common/hooks/useFocusableRef';
import SaveCancelButtons from '@/views/screens/Shipment/components/SaveCancelButtons';

import { ReactComponent as DeleteIcon } from '../../../../../../../../assets/icons/delete-icon.svg';
import IconButton from '../../../../../../../components/Buttons/IconButton';
import SvgConverter from '../../../../../../../components/Common/SvgConverter';
import FormInput from '../../../../../../../components/Inputs/FormInput';
import HiddenFormInput from '../../../../../../../components/Inputs/HiddenFormInput';
import Paper from '../../../../../../../components/Paper/Paper';

const OverChargeItemsCard = ({
  isLastItem = false,
  shadowVisible = true,
  itemKey,
  noteDetails,
  handleDelete,
  handleAddNew,
  totalItemsCount = 1,
  registerForm,
  handleSubmit,
  editMode = false,
  setEditMode,
  handleCancel,
  onFieldChange,
}) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (registerForm) {
      registerForm(itemKey, form);
      return () => {
        registerForm(itemKey, null);
      };
    }
  }, [form, itemKey, registerForm]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO: fix
  useEffect(() => {
    if (!noteDetails) {
      return;
    }

    form.setFieldsValue({
      ...noteDetails,
      billedAmount: Number.parseFloat(noteDetails.billedAmount),
      correctCharges: convertToFloat(noteDetails.correctCharges),
      overchargeAmount: Number.parseFloat(noteDetails.overchargeAmount),
      paidAmount: Number.parseFloat(noteDetails.paidAmount),
      recoveryAmount: convertToFloat(noteDetails.recoveryAmount),
    });
  }, [noteDetails]);

  const cardRef = useFocusableRef('#shipDate', { isEnabled: noteDetails?.isNew && !noteDetails?.isInitial });

  const paidAmount = Form.useWatch('paidAmount', form);
  const correctCharges = Form.useWatch('correctCharges', form);

  useEffect(() => {
    const paidAmountL = paidAmount ?? 0;
    const correctChargesL = correctCharges ?? 0;
    let diffAmount = paidAmountL - correctChargesL;
    diffAmount = diffAmount >= 0 ? diffAmount : diffAmount * -1;
    form.setFieldValue('overchargeAmount', diffAmount);
  }, [paidAmount, correctCharges, form]);

  const convertToFloatAmounts = (fieldName) => {
    const value = form.getFieldValue(fieldName);
    form.setFieldValue(fieldName, convertToFloat(value));
  };

  const isEditContext = Boolean(registerForm);
  const isFormEditable = isEditContext || editMode;

  const isNewItem = noteDetails?.isNew;
  const cardClassName = `border-muted ${!isLastItem ? 'mb-16' : ''} ${shadowVisible ? '' : 'shadow-none'}`;
  const cardStyle = isNewItem ? { backgroundColor: 'rgba(0, 123, 255, 0.05)', borderLeft: '3px solid #007bff' } : {};

  const handleFieldChange = () => {
    if (onFieldChange && !isNewItem) {
      onFieldChange();
    }
  };

  return (
    <div ref={cardRef}>
      <Paper className={cardClassName} style={cardStyle}>
        <Form
          onFinish={(data) => {
            handleSubmit(data, itemKey);
          }}
          initialValues={noteDetails}
          form={form}
        >
          <div className="border-bottom-muted pt-8 pb-10 px-16">
            {isNewItem && !isEditContext && editMode && (
              <div className="mb-8">
                <span className="badge bg-primary text-white px-8 py-4 f-12">New</span>
              </div>
            )}
            <Row justify="align-items-center" gutter={15}>
              <Col sm={6}>
                <DatePicker
                  label="Ship Date"
                  placeholder="Choose ship date"
                  name="shipDate"
                  editMode={isFormEditable}
                  disabledTargetDate={new Date().toISOString()}
                  autoFocus={isNewItem && !isEditContext && !noteDetails?.isInitial}
                  disableFuture
                  margin="m-0"
                  onChange={handleFieldChange}
                  rules={[
                    {
                      message: 'Please choose ship date',
                      required: true,
                    },
                  ]}
                />
              </Col>
              <Col sm={6}>
                <FormInput
                  label="Freight Bill / Pro Number"
                  placeholder="Enter Freight Bill / Pro Number"
                  margin="mb-0"
                  name="proNo"
                  editMode={isFormEditable}
                  onChange={handleFieldChange}
                  validateTrigger={['onChange', 'onBlur']}
                  rules={[
                    {
                      message: 'Please enter Freight Bill / Pro Number',
                      required: true,
                    },
                  ]}
                  maxLength={25}
                />
              </Col>
            </Row>
          </div>
          <div className="px-15 pt-10">
            <Row gutter={15}>
              <Col sm={6}>
                <FormInput
                  label="Package Number"
                  placeholder="Please enter package number"
                  name="packageNumber"
                  editMode={isFormEditable}
                  onChange={handleFieldChange}
                  maxLength={30}
                />
                <FormInput
                  label="Invoice Number"
                  placeholder="Please enter invoice number"
                  name="invoiceNumber"
                  editMode={isFormEditable}
                  onChange={handleFieldChange}
                  maxLength={25}
                />
              </Col>
              <Col sm={6}>
                <FormInput
                  label="Chargeable Weight"
                  placeholder="Please enter chargeable weight"
                  name="weight"
                  editMode={isFormEditable}
                  inputType="number"
                  onChange={handleFieldChange}
                />
                <FormInputNumber
                  label="Billed Amount"
                  placeholder="Please enter billed amount"
                  name="billedAmount"
                  editMode={isFormEditable}
                  inputType="number"
                  allowNegative
                  precision={2}
                  onChange={handleFieldChange}
                />
              </Col>
              <Col sm={6}>
                <FormInputNumber
                  label="Paid Amount"
                  placeholder="Please enter paid amount"
                  name="paidAmount"
                  editMode={isFormEditable}
                  inputType="number"
                  allowNegative
                  precision={2}
                  onChange={handleFieldChange}
                />
                <FormInput
                  label="Correct Charges"
                  placeholder="Please enter correct charges"
                  name="correctCharges"
                  editMode={isFormEditable}
                  inputType="number"
                  onChange={handleFieldChange}
                  onBlur={() => convertToFloatAmounts('correctCharges')}
                />
              </Col>
              <Col sm={6}>
                <FormInput
                  label="Overcharge Sum"
                  placeholder="Please enter overcharge sum"
                  name="overchargeAmount"
                  editMode={isFormEditable}
                  inputType="number"
                  margin="mb-15"
                  onChange={handleFieldChange}
                />
                <FormInput
                  label="Recovery Sum"
                  placeholder="Please enter recovery sum"
                  name="recoveryAmount"
                  editMode={isFormEditable}
                  inputType="number"
                  onChange={handleFieldChange}
                  onBlur={() => convertToFloatAmounts('recoveryAmount')}
                />
              </Col>
            </Row>
            <Col className="d-flex justify-content-end pb-10">
              {isEditContext ? (
                // EDIT mode: Delete button only (always editable)
                <Row gutter={8} align="middle">
                  {totalItemsCount > 1 && (
                    <Col>
                      <Popconfirm
                        placement="topRight"
                        title="Are you sure you want to delete this item?"
                        onConfirm={() => handleDelete(itemKey)}
                        okText="Delete"
                        cancelText="Cancel"
                        okType="danger"
                        autoFocus
                        getPopupContainer={(trigger) => trigger.parentElement}
                      >
                        <IconButton dataTestId={`delete-${itemKey}`}>
                          <SvgConverter Icon={DeleteIcon} color="iconDanger" />
                        </IconButton>
                      </Popconfirm>
                    </Col>
                  )}
                  <Col>
                    <Button className="ml-16" onClick={() => handleAddNew(itemKey)}>
                      Add New Item
                    </Button>
                  </Col>
                </Row>
              ) : (
                <Row gutter={8} align="middle">
                  <Col>
                    <SaveCancelButtons
                      editMode={editMode}
                      lightMode={false}
                      setEditMode={(val) => setEditMode?.(itemKey, val)}
                      handleRemove={() => handleDelete(itemKey)}
                      isNew={noteDetails.isNew}
                      idSuffix={`product-details-${itemKey}`}
                      isModified
                      onCancel={() => handleCancel?.(itemKey, noteDetails.isNew)}
                      hideDeleteButton={totalItemsCount === 1}
                    />
                  </Col>
                  <Col>
                    <Button className="ml-16" onClick={() => handleAddNew(itemKey)}>
                      Add New Item
                    </Button>
                  </Col>
                </Row>
              )}
            </Col>
          </div>
          <HiddenFormInput name="sequenceNo" />
          <HiddenFormInput name="shipDate" />
          <HiddenFormInput name="isNew" />
          <HiddenFormInput name="itemID" />
          <HiddenFormInput name="fbOcKey" />
        </Form>
      </Paper>
    </div>
  );
};

export default React.memo(OverChargeItemsCard);
